import { useState, useCallback, useMemo } from 'react';
import { BannerClient } from '../../../infrastructure/BannerClient';
import { getConfig } from '../../../config';

interface UploadingFile {
  name: string;
  progress: number;
  error: string | undefined;
}

export interface Banner {
  bannerId: string; // server will assign this field
  date: string; // YYYY-MM-DD format - server will assign this field
  imageUrl: string; // publicly accessible url for desktop
  mobileImageUrl?: string; // publicly accessible url for mobile devices
  tabletImageUrl?: string; // publicly accessible url for tablet devices
  title: string;
  description?: string;
  location: string; // path where the banner should be displayed (e.g., /, /trend, /brands)
  linkUrl?: string; // when the banner is clicked, it will redirect to the linkUrl
  active?: boolean; // server determines this based on date range and other factors
  fromDate: string; // YYYY-MM-DD format
  toDate: string; // YYYY-MM-DD format
  order: number; // order of the banner in the list
  createdAt?: string; // ISO timestamp - server managed
  updatedAt?: string; // ISO timestamp - server managed
}

// Add interface to match server response
export interface ListBannersResponse {
  banners: Banner[];
  lastEvaluatedKey?: string;
}

interface UseBanner {
  uploadingFiles: Record<string, UploadingFile>;
  uploadFile: (file: File) => Promise<string | null>;
  removeUploadingFile: (fileName: string) => void;
  createBanner: (banner: Banner) => Promise<Banner>;
  createBanners: (banners: Banner[]) => Promise<Banner[]>;
  updateBanner: (bannerId: string, banner: Partial<Banner>) => Promise<Banner>;
  deleteBanner: (bannerId: string) => Promise<void>;
  getBanner: (bannerId: string) => Promise<Banner>;
  listBanners: (params?: {
    active?: boolean;
    lastEvaluatedKey?: string;
    limit?: number;
  }) => Promise<ListBannersResponse>;
  updateBannerOrders: (banners: Array<{ bannerId: string; order: number }>) => Promise<void>;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_FILE_TYPES = ['image/webp', 'video/mp4'];

export const useBanner = (): UseBanner => {
  const config = getConfig();
  const bannerClient = useMemo(() => new BannerClient(config.apiUrl), [config.apiUrl]);
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, UploadingFile>>({});

  /**
   * Validates file size and type
   */
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File type ${file.type} is not supported. Please upload WebP images or MP4 videos.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds the maximum limit of 50MB.`;
    }
    return null;
  };

  /**
   * Uploads a file to S3 using the presigned URL
   * Uses a simple fetch request
   */
  const uploadToS3 = async (file: File, presignedUrl: string): Promise<void> => {
    try {
      // Report start of upload
      setUploadingFiles((prev) => ({
        ...prev,
        [file.name]: {
          name: file.name,
          progress: 0,
          error: undefined,
        },
      }));

      // Simple progress update at 50%
      setUploadingFiles((prev) => ({
        ...prev,
        [file.name]: {
          name: file.name,
          progress: 50,
          error: undefined,
        },
      }));

      // Simple direct upload without streaming
      console.info(`uploadToS3 with presignedUrl: ${presignedUrl}, file ${file.name}, file type: ${file.type}`);
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!response.ok) {
        console.error('Upload error details:', response.status, response.statusText);
        throw new Error(`Upload failed with status ${response.status}`);
      }

      // Update progress to 100% on success
      setUploadingFiles((prev) => ({
        ...prev,
        [file.name]: {
          name: file.name,
          progress: 100,
          error: undefined,
        },
      }));

      // Clear the uploading state after a brief delay to show 100%
      setTimeout(() => {
        setUploadingFiles((prev) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [file.name]: _removed, ...rest } = prev;
          return rest;
        });
      }, 500);
    } catch (error) {
      // Simple error handling - no retries
      console.error('Upload error:', error);
      setUploadingFiles((prev) => ({
        ...prev,
        [file.name]: {
          name: file.name,
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed',
        },
      }));
      throw error;
    }
  };

  /**
   * Uploads a file to S3 via presigned URL
   * 1. Validates the file
   * 2. Gets a presigned URL from the API
   * 3. Uploads the file to S3
   * 4. Returns the S3 URL or presigned URL
   */
  const uploadFile = async (file: File): Promise<string | null> => {
    // Validate file first
    const validationError = validateFile(file);
    if (validationError) {
      setUploadingFiles((prev) => ({
        ...prev,
        [file.name]: {
          name: file.name,
          progress: 0,
          error: validationError,
        },
      }));
      return null;
    }

    try {
      // Get pre-signed URL from banner client, the bucket is defined in server side
      const response = await bannerClient.getPresignedUrl(file.name);
      console.info('getPresignedUrl', response);

      // Upload file to S3 using our enhanced uploader
      await uploadToS3(file, response.presignedUrl);

      const imageUrl = response.presignedUrl.split('?')[0]; // accessable image url
      console.info('uploadToS3', imageUrl);
      console.info('uploadToS3', response.s3Key);

      return imageUrl || null;
    } catch (error) {
      // Handle errors
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';

      setUploadingFiles((prev) => ({
        ...prev,
        [file.name]: {
          name: file.name,
          progress: prev[file.name]?.progress || 0,
          error: errorMessage,
        },
      }));

      console.error('Error uploading file:', errorMessage);
      return null;
    }
  };

  /**
   * Removes a file from the uploading state
   */
  const removeUploadingFile = useCallback((fileName: string) => {
    setUploadingFiles((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [fileName]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Creates a new banner
   */
  const createBanner = useCallback(
    async (banner: Banner): Promise<Banner> => {
      try {
        return await bannerClient.createBanner(banner);
      } catch (error) {
        console.error('Failed to create banner:', error);
        throw error;
      }
    },
    [bannerClient],
  );

  /**
   * Creates multiple banners with the same date range
   */
  const createBanners = useCallback(
    async (banners: Banner[]): Promise<Banner[]> => {
      try {
        return await bannerClient.createBanners(banners);
      } catch (error) {
        console.error('Failed to create banners:', error);
        throw error;
      }
    },
    [bannerClient],
  );

  /**
   * Updates an existing banner
   */
  const updateBanner = useCallback(
    async (bannerId: string, banner: Partial<Banner>): Promise<Banner> => {
      try {
        return await bannerClient.updateBanner(bannerId, banner);
      } catch (error) {
        console.error(`Failed to update banner with ID ${bannerId}:`, error);
        throw error;
      }
    },
    [bannerClient],
  );

  /**
   * Deletes a banner
   */
  const deleteBanner = useCallback(
    async (bannerId: string): Promise<void> => {
      try {
        await bannerClient.deleteBanner(bannerId);
      } catch (error) {
        console.error(`Failed to delete banner with ID ${bannerId}:`, error);
        throw error;
      }
    },
    [bannerClient],
  );

  /**
   * Gets a single banner by ID
   */
  const getBanner = useCallback(
    async (bannerId: string): Promise<Banner> => {
      try {
        return await bannerClient.getBanner(bannerId);
      } catch (error) {
        console.error(`Failed to get banner with ID ${bannerId}:`, error);
        throw error;
      }
    },
    [bannerClient],
  );

  /**
   * Lists all banners with optional filtering
   */
  const listBanners = useCallback(
    async (params?: { active?: boolean; lastEvaluatedKey?: string; limit?: number }): Promise<ListBannersResponse> => {
      try {
        return await bannerClient.listBanners(params);
      } catch (error) {
        console.error('Failed to list banners:', error);
        throw error;
      }
    },
    [bannerClient],
  );

  /**
   * Updates banner orders in bulk
   */
  const updateBannerOrders = useCallback(
    async (banners: Array<{ bannerId: string; order: number }>): Promise<void> => {
      try {
        await bannerClient.updateBannerOrders(banners);
      } catch (error) {
        console.error('Failed to update banner orders:', error);
        throw error;
      }
    },
    [bannerClient],
  );

  return {
    uploadingFiles,
    uploadFile,
    removeUploadingFile,
    createBanner,
    createBanners,
    updateBanner,
    deleteBanner,
    getBanner,
    listBanners,
    updateBannerOrders,
  };
};
